import { useCallback, useState } from 'react';
import { isMobile } from 'react-device-detect';
import shield_icon from 'assets/green_shield_2.svg';
import logo from 'assets/mask_logo_icon.svg?url';
import { useMappedState } from 'redux-react-hook';
import { ITokenPackage, useGetTokenPricesQuery } from 'services/api/tokenApi';
import styled from 'styled-components';
import { AnimatedLogo, Circle, Wrapper } from 'ui-kit/FullScreenSpinner/styled';
import Toggler from 'ui-kit/Toggler/Toggler';

import ChoosePlanSection from './components/ChoosePlanSection';

interface IIconProps {
  src: string;
}

interface IMainContentModalSectionProps {
  onClose: () => void;
}

const CustomSpinner = ({ color }: { color?: string }) => {
  return (
    <SpinnerWrapper>
      <AnimatedLogo src={logo} alt="Loading..." />
      <Circle color={color} />
    </SpinnerWrapper>
  );
};

const MainContentModalSection: React.FC<IMainContentModalSectionProps> = (props) => {
  const { onClose } = props;

  const [selectedPlan, setSelectedPlan] = useState<ITokenPackage | null>(null);
  const [useSavedCard, setUseSavedCard] = useState<boolean>(true);
  const { data, isLoading, isSuccess } = useGetTokenPricesQuery();

  const packages = data?.packages ?? [];

  const { currentUser } = useMappedState(
    useCallback(state => {
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
      };
    }, [])
  );

  const isG2aPaymentProvider = currentUser?.subscription?.paymentProvider === 'g2a';

  return (
    <Root >
      <Header>
        <Title>Top up tokens</Title>
        <Subtitle>
          Select the token package that best matches your ambitions.
        </Subtitle>
      </Header>
      {isLoading && <LoadingContainer><CustomSpinner color="#FF7E55" /></LoadingContainer>}
      {isSuccess && (
        <>
          <MainContent>
            {/* <CostsSection /> */}
            {!isG2aPaymentProvider && (
              <ToggleSection>
                <ToggleRow>
                  <CustomToggleText>Use saved card for payments</CustomToggleText>
                  <Toggler
                    isActive={useSavedCard}
                    onToggle={() => setUseSavedCard((prev) => !prev)}
                  />
                </ToggleRow>
              </ToggleSection>
            )}
            <ChoosePlanSection
              packages={packages}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              useSavedCard={isG2aPaymentProvider ? false : useSavedCard}
              onClose={onClose}
            />
            <PaymentInfoSectionContainer isMobile={isMobile}>
              <PaymentInfoRow>
                <Icon src={shield_icon} />
                <PaymentInfoDescription>Transactions are processed securely.</PaymentInfoDescription>
              </PaymentInfoRow>
            </PaymentInfoSectionContainer>
          </MainContent>
        </>
      )}
    </Root >
  );
};

export default MainContentModalSection;

const Root = styled.div`
  width: 52%;
  height: 100%;
  padding: 32px;
  background: #1D1D1D;
  border-radius: 0 12px 12px 0;
  display: flex;
  flex-direction: column; 

  @media (max-width: 768px) {
    width: 100vw;
    border-radius: 12px;
    padding: 24px;
  }
`;

const SpinnerWrapper = styled(Wrapper)`
  background: transparent;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex-grow: 1;

  @media (max-height: 700px) {
    gap: 10px;
  }
`;

const Title = styled.span`
  font-family: Raleway;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  padding-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 24px;
    text-align: center;
  }

  @media (max-height: 700px) {
    font-size: 16px;
    line-height: 18px;
  }
`;

const Subtitle = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
`;

const PaymentInfoRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const Icon = styled.img<IIconProps>`
  width: 14px;
  height: fit-content;
`;

const PaymentInfoDescription = styled.span`
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  color: #A9A9A9;
`;


const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const PaymentInfoSectionContainer = styled.div<{ isMobile: boolean }>`
  margin-top: ${({ isMobile }) => isMobile ? 'unset' : 'auto'};
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ToggleSection = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #282828;
  border-radius: 12px;
  padding: 16px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CustomToggleText = styled.span`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  color: #FFF;
`;
